
export enum AppealStatus {
  WorkInProgress = "Work In Progress",
  ReadyForBoard = "Ready For Board",
  Stipulated = "Stipulated",
  JudgmentIssued = "Judgment Issued",
  Withdrawn = "Withdrawn",
};

export enum UserRoles {
  COUNTY_ADMIN = "CountyAdmin",
  COUNTY_CLERK = "CountyClerk",
  COUNTY_COMMISSIONER = "CountyCommissioner",
  SOLICITOR = "Solicitor",
  ASSESSOR = "Assessor",
  ADMIN = "Admin",
  AUTHORIZED_ATTORNEY = "AuthorizedAttorney",
};

export type ContactAttributes = {
  notify?: boolean;
  firstName?: string; //contains full name
  address?: string;
  city?: string;//contains city, state
  zipCode?: string;
  emailAddress?: string;
  dayTimePhone?: string; //assumed to be cell phone for SMS
}

export type MainContact = ContactAttributes & {
  mainEmailCheckbox: boolean;
  mainPhoneCheckbox: boolean;
  contactEmailCheckbox?: boolean;
  contactPhoneCheckbox?: boolean;
}

export type AdditionalContact = ContactAttributes & {
  isAdditionalContact?: boolean;
  aCLawyerLicense?: string;
  contactEmailCheckbox?: boolean;
  contactPhoneCheckbox?: boolean;
  isLawyer?: boolean;
}
export type AuthorizedAttorneyContact = ContactAttributes & {
  isAuthorizedAttorneyContact?: boolean;
  authorizedAttorneyEmailCheckbox?: boolean;
  authorizedAttorneyPhoneCheckbox?: boolean;
  aALawyerLicense?: string;
}
export type MunicipalSolicitorContact = ContactAttributes & {
  isThirdPartyContact?: boolean;
  thirdPartyEmailCheckbox?: boolean;
  thirdPartyPhoneCheckbox?: boolean;
}

export type UserContactDefault = {
  isLawyer: boolean;
  emailAddress: string;
  notifyBySMS: boolean;
  dayTimePhone: string;
  notifyByEmail: boolean;
}

export type SolicitorContact = ContactAttributes & {
  isActive: boolean;
}

export type UserPreferences = {
  contactInfo: ContactDefaults;
}

export type ContactDefaults = {
  contact: UserContactDefault;
  additionalContact: AdditionalContact;
  solicitorContact: SolicitorContact;
}

export type PropertyClassification = {
  key: string;
  label: string;
}

export type ClassificationAppeal = {
  appealing: boolean;
  currentClass?: PropertyClassification;
  requestedClass?: PropertyClassification;
}

export type ScanDenial = {
  deferred: boolean;
  fileName: string;
  url: string;
}


export type BatchAppeal = {
  fees: AppealFees;
  property: PropertyRecord;
  valuation: ValuationAppeal;
}

export type NewAppeal = BatchAppeal & {
  appealYear: number;
  appVersion: string;
  classification: ClassificationAppeal;
  additionalContact: AdditionalContact;
  authorizedAttorneyContact: AuthorizedAttorneyContact;
  contact: MainContact;
  county: County;
  creatorObj: AppealCreator;
  thirdPartyContact: MunicipalSolicitorContact;
  creditCardPayment: CreditCardPayment;
  crossAppeal: CrossAppeal;
  deduction: DeductionAppeal;
  deleted: boolean;
  entryMethod: EntryMethod;
  entryPaymentMethod: PaymentMethod;
  filingDescription: string;
  geolocation: object;
  hearingDate: string | null;
  hearingLocation: string | null;
  hearingType?: HearingType;
  improvementRecord: PropertyImprovement;
  judgment?: null;
  municipality: Municipality;
  notes: string | null;
  receiptId: string;
  scanFile?: string;
  scanDenial?: ScanDenial;
  stipulation?: object;
  tenAppeal: boolean;
  tenRecords: AddOmitRecord[] | null;
  withdrawn?: boolean;
  isBatchTemplate?: boolean;
  status?: AppealStatus;
  primaryAppealNumber?: string;
}

export type AppealRecord = NewAppeal & {
  appealNumber: string;
  database_id: string;
  status: AppealStatus;
  submitDate: string;
  evidenceFreezeDate?: string;
  hearingDateFinalized?: string;
  reasonForWithdrawal?: string;
  hearingOnHold?: boolean;
}

export type PropertyImprovement = {
  requestedAdditionAbatement?: number;
  requestedAdditionAmount?: number;
  requestedAdditionImprovement?: number;
  requestedAdditionLand?: number;
  requestedAdditionDate?: string;
  requestedAdditionMonth?: number;
  originalRecord?: AddOmitRecord;
  propertyClass?: PropertyClassification;
  listType?: string;
  taxYear?: number;
}

export type PropertyRecord = {
  address: string;
  blq: string;
  cityState: string;
  classification: PropertyClassification;
  currentAssessment: Assessment;
  key: string;
  lotSize: string;
  owner: string;
  purchaseDate?: string;
  purchasePrice?: number;
  relevantProperty: string;
  tenData: AddOmitRecord[] | null;
  zip: string;
}

export type Assessment = {
  abatement: number;
  buildingImprovement: number;
  land: number;
  total: number;
}

export type AddOmitRecord = {
  propertyClass: PropertyClassification;
  additionAbatement: number;
  additionImprovement: number;
  additionLand: number;
  additionProrated?: number;
  additionTotal?: number;
  blq: string;
  completionDate: string;
  listType: string;
  totalMonthsAssessed: string;
  type: string;
  taxYear: number;
}

export type AppealFees = {
  convenienceFee: number;
  deductionExemptionFee: number;
  propertyClassificationFee: number;
  propertyValuationFee: number;
  total: number;
}

export type TaxAdministrator = {
  name: string;
  title: string;
}

export type CountyCommissioner = {
  name: string;
  title: string;
}

export type TaxBoard = {
  name: string;
  address: string[];
}

export type County = {
  key: string;
  label: string;
  taxAdministrator?: TaxAdministrator;
  taxBoard?: TaxBoard;
}

export type CountyDefinition = County & {
  active: boolean;
  municipalities: Municipality[];
}

export type CreditCardPayment = {
  paidAppeal: boolean;
  transId?: string;
  authCode?: string;
  refId?: string;
  paymentAmount?: number;
  transactionResponse?: CreditCardTransactionResponse;
  batchIndicator?: boolean;
}

export type CreditCardTransactionResponse = {
  SupplementalDataQualificationIndicator: number;
  accountNumber: string;
  accountType: string;
  authCode: string;
  avsResultCode: string;
  cavvResultCode: string;
  cvvResultCode: string;
  messages: [CreditCardTransactionMessage];
  networkTransId: string;
  responseCode: string;
  testRequest: string;
  transId: string;
}

export type CreditCardTransactionMessage = {
  code: string;
  description: string;
}

export type CrossAppeal = {
  isCrossAppeal: boolean;
  isCrossedAppeal?: boolean;
  otherAppeal?: string;
}

export type DeductionAppeal = {
  abatement: boolean;
  disabledVeteranExemption: boolean;
  seniorCitizenOrDisabledPerson: boolean;
  veteranDeduction: boolean;
}

export enum EntryMethod {
  ONLINE = "ONLINE",
  PAPER = "PAPER"
}

export enum HearingType {
  IN_PERSON = "InPerson",
  SUMMARY = "Summary",
  VIRTUAL = "Virtual"
}

export type Municipality = {
  key: string;
  label: string;
}

export type Appeal = {
  _DATABASE_ID: string;
  _APPEAL_NUMBER: string;
  _UUID: string;
  APPEAL: AppealRecord;
  JUDGEMENT: JudgementRecord;
  STIPULATION: StipulationRecord;
  AWOP?: AWOPRecord;
}

export enum PaymentMethod {
  ONLINE_CARD = "Online Card",
  NONE = "None",
  CASH = "Cash",
  CHECK = "Check",
  MONEY_ORDER = "Money Order",
  CARD_PRESENT = "Card Present"
}

export type LocalError = {
  name: string;
  message: string;
}

export type UserConfigurationResponse = {
  user?: UserConfiguration;
}

export type UserConfiguration = {
  _UUID?: string;
  Created?: string;
  Group?: string;
  Email?: string;
  Fullname?: string;
  Phone?: string;
  County?: County;
  Municipalities?: Array<Municipality>;
  signedDate?: number;
}

export type AppealApprover = UserConfiguration & {
  filedBySolicitor?: boolean;
}

export type AppealCreator = UserConfiguration & {
  approved?: boolean;
  approverObject?: AppealApprover;
  isLawyer?: boolean;
  filedBySolicitor?: boolean;
}

export type CognitoUser = {
  username: string;
  email: string;
  phoneNumber?: string;
}

export type AWOPRecord = {
  awop: boolean;
  appellantObj?: UserConfiguration;
  assessorObj: UserConfiguration;
  solicitorObj?: UserConfiguration;
  status: AWOPStatus;
  fileName?: string;
  awopFile?: string;
}


export type ValuationAppeal = {
  appealing: boolean;
  purchaseDate?: string;
  purchasePrice?: number;
  reason?: string;
  requestedTotal?: number;
  taxCourtPending?: boolean;
  awop?: boolean;
}

export type StipulationRecord = {
  assessorObj: UserConfiguration;
  basisForSettlement: string;
  requestedAdditionMonth: number;
  status: StipulationStatus;
  stipAmount: StipulationAmount;
  propertyClass?: PropertyClassification;
  fileName?: string;
  stipFile?: string;
  appellantObj?: UserConfiguration;
  solicitorObj?: UserConfiguration;
}

export type StipulationAmount = {
  landAmount: number;
  buildingAmount: number;
  abatementAmount: number;
  total: number;
}

export type JudgementRecord = {
  judgementResult: JudgementResult;
  judgementResultTwo: JudgementResult | null;
  judgementExplanation: string;
  judgementPropertyClass: PropertyClassification;
  presentSelected: string[];
  judgementAddedDate: string;
  judgementMailDate: string;
  judgementAmount: JudgementAmount;
  taxCourtTaxYear?: number;
  revisedAcreage?: number;
}

export type JudgementResult = {
  key: string;
  label: string;
}

export type JudgementAmount = {
  landAmount: number;
  buildingAmount: number;
  abatementAmount: number;
  monthAmount?: number;
  prTotal?: number;
  total: number;
}

export type UserActivity = {
  id: string;
  username: string;
  time: string;
  data?: object;
  appeal?: AppealRecord;
  stage: number;
  button: string;
}

export type NewAppealRequest = {
  appeal: NewAppeal;
  count: number;
  appealYear: number;
}

export type AddAppealEvidenceRequest = {
  evidence: AppealEvidenceRequest;
}

export type AppealEvidenceRequest = {
  "title": string;
  "fileName": string;
  "fileUrl": string;
  "assessor": boolean;
}

export type AppealEvidence = AppealEvidenceRequest & {
  "_EID": string;
  "_APPEAL_NUMBER": string;
  "_UUID": string;
}

export type AddAppealComparableRequest = {
  comparable: AppealComparableRequest;
}

export type AppealComparableRequest = {
  qual: string;
  address: string;
  salePrice: string | number;
  saleDate: string;
  assessor: boolean;
}

export type AppealComparable = AppealComparableRequest & {
  "_CID": string;
  "_APPEAL_NUMBER": string;
  "_UUID": string;
}

export type AppealEvent = {
  "_APPEAL_NUMBER": string;
  "time": string;
  "_EVID": string;
  "eventText": string;
  "_UUID": string;
}


export enum StipulationStatus {
  "PROPOSED" = "PROPOSED",
  "APPELLANT_APPROVED" = "APPELLANTAPPROVED",
  "APPELLANT_REJECTED" = "APPELLANTREJECTED",
  "SOLICITOR_APPROVED" = "SOLICITORAPPROVED",
  "SOLICITOR_REJECTED" = "SOLICITORREJECTED",
}

export enum AWOPStatus {
  "PROPOSED" = "PROPOSED",
  "APPELLANT_APPROVED" = "APPELLANTAPPROVED",
  "APPELLANT_REJECTED" = "APPELLANTREJECTED",
  "SOLICITOR_APPROVED" = "SOLICITORAPPROVED",
  "SOLICITOR_REJECTED" = "SOLICITORREJECTED",
}

export type SelectOption = {
  label: string;
  value: string;
}

export type AppealSeason = {
  start?: string;
  end?: string;
}

export type MunicipalityAppealSeason = AppealSeason & {
  municipalityKey: string;
}


export enum AppealCategory {
  "VALUE_CLASS" = "Value/class",
  "DEDUCTION" = "Deduction",
  "ADD_OMIT" = "Addition/omission",
};

export type SeasonConfiguration = {
  season: AppealSeason;
  judgmentSinceFilter?: string;
  municipalityOverrides: MunicipalityAppealSeason[];
}

export type CountyTaxAdministration = {
  taxBoard: TaxBoard;
  taxAdministrator: TaxAdministrator;
  countyCommissioners: CountyCommissioner[];
  meetingRooms: string[];
}

export type CountyConfigurationResponse = {
  countyConfiguration: CountyConfiguration;
}

export type CountyOptions = {
  freezeEvidenceBasedOnOriginalHearingDate: boolean;
}

export type CountyConfiguration = {
  countyTaxAdministration: CountyTaxAdministration;
  countyOptions: CountyOptions;
  valueClassSeason?: SeasonConfiguration;
  addOmitSeason?: SeasonConfiguration;
}

export interface FieldData<Type> {
  touched?: boolean;
  validating?: boolean;
  validated?: boolean;
  value?: Type;
  errors?: string[];
  warnings?: string[];
  name: string[];
}

export type AppealJudgement = {
  appealNumber: string;
  currentAppealStatus: string;
  judgement: Partial<JudgementRecord>;
}
export type MultipleJudgementRequest = {
  judgementStatus?: AppealStatus;
  sendNotifications: boolean;
  appealJudgements: AppealJudgement[];
}
